import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { useRightClickMenu } from "./RightClickMenuContext";
import { addToClipboard } from "../../api/universal";
import { MessageSquare, Save, Copy } from "lucide-react";

/* ── Inject context-menu animation keyframes once ── */
const injectMenuAnimation = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const s = document.createElement("style");
    s.textContent = `
      @keyframes ctx-menu-in {
        from { opacity: 0; transform: scale(0.92); }
        to   { opacity: 1; transform: scale(1); }
      }
      .ctx-menu-enter {
        animation: ctx-menu-in 120ms cubic-bezier(0.16, 1, 0.3, 1) both;
      }
    `;
    document.head.appendChild(s);
  };
})();

/* ═══════════════════════════════════════════════════════════════
   RightClickMenu

   Renders a context-aware right-click menu. Items come from two sources:
   1. Zone-specific items — registered by the component that owns the
      right-clicked element (e.g. Edit View, Delete View for feed items)
   2. Global items — always available when text is selected
      (Ask Kernel, Copy, Save)

   QoL features:
   - #3:  Portals to document.body so it escapes any stacking context
   - #5:  Measures itself and flips direction if it would overflow viewport
   - #10: Full keyboard navigation (Arrow keys, Enter, Escape)
   ═══════════════════════════════════════════════════════════════ */

const RightClickMenu = () => {
  const { menuState, closeMenu, getZoneItems } = useRightClickMenu();
  const { visible, position, zone, payload, selectedText } = menuState;

  /* ── #5: Smart positioning — clamp to viewport ── */
  const menuRef = useRef(null);
  const [finalPos, setFinalPos] = useState(position);
  const [transformOrigin, setTransformOrigin] = useState("top left");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    if (!visible) {
      setFocusedIndex(-1);
      return;
    }
    setFinalPos(position);
    // Default: menu opens below-right of click
    let originX = "left";
    let originY = "top";

    // After render, measure and adjust if overflowing
    const raf = requestAnimationFrame(() => {
      const el = menuRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pad = 8;

      let x = position.x;
      let y = position.y;

      if (x + rect.width + pad > vw) {
        x = Math.max(pad, x - rect.width);
        originX = "right";
      }
      if (y + rect.height + pad > vh) {
        y = Math.max(pad, y - rect.height);
        originY = "bottom";
      }

      setTransformOrigin(`${originY} ${originX}`);

      if (x !== position.x || y !== position.y) {
        setFinalPos({ x, y });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [visible, position]);

  const hasSelection = selectedText.trim().length > 0;

  /* ── Zone-specific items ── */
  const zoneItems = useMemo(() => {
    if (!zone) return [];
    const items = getZoneItems(zone);
    return items.filter((item) => {
      if (item.hidden && item.hidden(payload)) return false;
      return true;
    });
  }, [zone, payload, getZoneItems]);

  /* ── Global items (text-selection based) ── */
  const globalItems = useMemo(
    () => [
      // {
      //   label: "Ask Kernel",
      //   icon: MessageSquare,
      //   action: () => {
      //     // TODO: wire up Ask Kernel action
      //   },
      //   requiresSelection: true,
      // },
      {
        label: "Save",
        icon: Save,
        action: () => {
          addToClipboard(selectedText).then((r) => console.log(r));
        },
        requiresSelection: true,
      },
      {
        label: "Copy",
        icon: Copy,
        action: async () => {
          try {
            await navigator.clipboard.writeText(selectedText);
          } catch (err) {
            console.error("Failed to copy text:", err);
          }
        },
        requiresSelection: true,
      },
    ],
    [selectedText],
  );

  const visibleGlobalItems = globalItems.filter(
    (item) => !item.requiresSelection || hasSelection,
  );

  const hasZoneItems = zoneItems.length > 0;
  const hasGlobalItems = visibleGlobalItems.length > 0;
  const showDivider = hasZoneItems && hasGlobalItems;

  /* ── Build flat list of all actionable items for keyboard nav ── */
  const allItems = useMemo(() => {
    const items = [];
    zoneItems.forEach((item, i) => {
      if (!item.disabled) items.push({ type: "zone", index: i, item });
    });
    visibleGlobalItems.forEach((item, i) => {
      items.push({ type: "global", index: i, item });
    });
    return items;
  }, [zoneItems, visibleGlobalItems]);

  /* ── #10: Keyboard navigation ── */
  const executeItem = useCallback(
    (entry) => {
      if (!entry) return;
      const { type, item } = entry;
      if (type === "zone") {
        item.action(payload);
      } else {
        item.action();
      }
      closeMenu();
    },
    [payload, closeMenu],
  );

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1;
            return next >= allItems.length ? 0 : next;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? allItems.length - 1 : next;
          });
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < allItems.length) {
            executeItem(allItems[focusedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          closeMenu();
          break;
        default:
          break;
      }
    };

    // Use capture so we get it before the global Escape handler
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [visible, focusedIndex, allItems, executeItem, closeMenu]);

  /* ── Scroll focused item into view ── */
  useEffect(() => {
    if (focusedIndex < 0 || !menuRef.current) return;
    const items = menuRef.current.querySelectorAll("[data-menu-item]");
    if (items[focusedIndex]) {
      items[focusedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  /* ── Auto-close when cursor drifts away ── */
  useEffect(() => {
    if (!visible) return;

    const CLOSE_DISTANCE = 100;

    const handleMouseMove = (e) => {
      const el = menuRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const dx = Math.max(rect.left - e.clientX, e.clientX - rect.right, 0);
      const dy = Math.max(rect.top - e.clientY, e.clientY - rect.bottom, 0);
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > CLOSE_DISTANCE) closeMenu();
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [visible, closeMenu]);

  if (!visible) return null;
  if (!hasZoneItems && !hasGlobalItems) return null;

  // Inject animation CSS on first render
  injectMenuAnimation();

  // Derive which flat-list index each rendered item corresponds to
  let flatIdx = 0;

  const menu = (
    <ul
      ref={menuRef}
      role="menu"
      className={[
        "fixed z-[9999] min-w-[170px] max-w-[260px] py-1.5 rounded-lg overflow-hidden",
        "bg-white/95 dark:bg-[#0B0F14]/95",
        "border border-slate-200/70 dark:border-[#0EA5E9]/20",
        "shadow-xl backdrop-blur-xl",
        "ctx-menu-enter",
      ].join(" ")}
      style={{
        top: `${finalPos.y}px`,
        left: `${finalPos.x}px`,
        transformOrigin,
      }}
    >
      {/* ── Zone-specific items ── */}
      {zoneItems.map((item, index) => {
        const Icon = item.getIcon ? item.getIcon(payload) : item.icon;
        const resolvedLabel = item.getLabel
          ? item.getLabel(payload)
          : item.label;
        const isDanger = item.variant === "danger";
        const isDisabled = item.disabled;
        const currentFlat = isDisabled ? -1 : flatIdx++;
        const isFocused = currentFlat === focusedIndex;

        return (
          <li
            key={`zone-${index}`}
            role="menuitem"
            data-menu-item
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) {
                item.action(payload);
                closeMenu();
              }
            }}
            className={[
              "flex items-center gap-3 px-3 py-2 transition-all duration-150 outline-none",
              isDisabled
                ? "cursor-not-allowed text-slate-400 dark:text-gray-600"
                : isDanger
                  ? [
                      "cursor-pointer text-slate-700 dark:text-gray-200",
                      isFocused
                        ? "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                        : "hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400",
                    ].join(" ")
                  : [
                      "cursor-pointer text-slate-700 dark:text-gray-200",
                      isFocused
                        ? "bg-slate-100 dark:bg-[#0EA5E9]/10 text-slate-900 dark:text-gray-100"
                        : "hover:bg-slate-100 dark:hover:bg-[#0EA5E9]/10 hover:text-slate-900 dark:hover:text-gray-100",
                    ].join(" "),
            ].join(" ")}
          >
            {Icon && (
              <Icon
                className={[
                  "w-4 h-4 shrink-0 transition-colors duration-150",
                  isDisabled
                    ? "text-slate-300 dark:text-gray-700"
                    : isDanger && isFocused
                      ? "text-red-400"
                      : "text-slate-400 dark:text-gray-500",
                ].join(" ")}
                strokeWidth={2}
              />
            )}
            <span className="text-sm">{resolvedLabel}</span>
          </li>
        );
      })}

      {/* ── Divider ── */}
      {showDivider && (
        <li
          role="separator"
          className="my-1 mx-2 border-t border-slate-200/60 dark:border-slate-700/40"
        />
      )}

      {/* ── Global items ── */}
      {visibleGlobalItems.map((item, index) => {
        const Icon = item.icon;
        const currentFlat = flatIdx++;
        const isFocused = currentFlat === focusedIndex;

        return (
          <li
            key={`global-${index}`}
            role="menuitem"
            data-menu-item
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              item.action();
              closeMenu();
            }}
            className={[
              "flex items-center gap-3 px-3 py-2 transition-all duration-150 outline-none",
              hasSelection
                ? [
                    "cursor-pointer text-slate-700 dark:text-gray-200",
                    isFocused
                      ? "bg-slate-100 dark:bg-[#0EA5E9]/10 text-slate-900 dark:text-gray-100"
                      : "hover:bg-slate-100 dark:hover:bg-[#0EA5E9]/10 hover:text-slate-900 dark:hover:text-gray-100",
                  ].join(" ")
                : "cursor-not-allowed text-slate-400 dark:text-gray-600",
            ].join(" ")}
          >
            <Icon
              className={[
                "w-4 h-4 shrink-0",
                hasSelection
                  ? isFocused
                    ? "text-[#0EA5E9]"
                    : "text-slate-400 dark:text-gray-500"
                  : "text-slate-300 dark:text-gray-700",
              ].join(" ")}
              strokeWidth={2}
            />
            <span className="text-sm">{item.label}</span>
          </li>
        );
      })}
    </ul>
  );

  return menu;
};

export default RightClickMenu;
